import { useEffect, useMemo, useState } from 'react';
import { applyFeaturePackage, getFeatureCatalog, getFeaturePackages, getFeaturePortals, saveFeaturePackage } from '../../api/feature-management-api';
import { useFeedback } from '../../components/Feedback';
import { ErrorMessage, Loading } from '../../components/Loading';
import type { FeatureCatalogItem, FeaturePackage, PortalFeatureSummary } from '../../models/feature-management';

function byCategory(items: FeatureCatalogItem[]) {
  const groups = new Map<string, FeatureCatalogItem[]>();
  for (const item of items) groups.set(item.Category, [...(groups.get(item.Category) ?? []), item]);
  return [...groups.entries()];
}

export function FeaturePackagesPage() {
  const { notify } = useFeedback();
  const [catalog, setCatalog] = useState<FeatureCatalogItem[]>();
  const [packages, setPackages] = useState<FeaturePackage[]>();
  const [portals, setPortals] = useState<PortalFeatureSummary[]>([]);
  const [applyPackageId, setApplyPackageId] = useState<number>();
  const [selectedPortals, setSelectedPortals] = useState<Set<number>>(new Set());
  const [dirtyPackageIds, setDirtyPackageIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>();

  const load = async () => {
    setError(undefined);
    try {
      const [c, p, ps] = await Promise.all([getFeatureCatalog(), getFeaturePackages(), getFeaturePortals()]);
      setCatalog(c);
      setPackages(p);
      setPortals(ps);
      setApplyPackageId((current) => current ?? p[0]?.ID);
      setDirtyPackageIds(new Set());
    } catch (e) { setError(e); }
  };
  useEffect(() => { void load(); }, []);

  const groups = useMemo(() => byCategory(catalog ?? []), [catalog]);
  const mandatory = useMemo(() => new Set((catalog ?? []).filter((x) => x.IsMandatory).map((x) => x.FeatureKey)), [catalog]);

  const toggle = (packageId: number, key: string, checked: boolean) => {
    if (mandatory.has(key) && !checked) return;
    setPackages((current) => current?.map((pkg) => pkg.ID === packageId
      ? { ...pkg, FeatureKeys: checked ? [...new Set([...pkg.FeatureKeys, key])] : pkg.FeatureKeys.filter((x) => x !== key) }
      : pkg));
    setDirtyPackageIds((current) => new Set(current).add(packageId));
  };

  const saveAll = async () => {
    const changed = (packages ?? []).filter((pkg) => dirtyPackageIds.has(pkg.ID));
    if (!changed.length) { notify('تغییری برای ذخیره وجود ندارد.', 'info'); return; }
    setSaving(true);
    try {
      const versions = new Map<number, number>();
      for (const pkg of changed) {
        const response = await saveFeaturePackage(pkg.ID, pkg.FeatureKeys);
        versions.set(pkg.ID, response.DefinitionVersion);
      }
      setPackages((current) => current?.map((pkg) => versions.has(pkg.ID) ? { ...pkg, DefinitionVersion: versions.get(pkg.ID) ?? pkg.DefinitionVersion } : pkg));
      setDirtyPackageIds(new Set());
      notify(`ترکیب امکانات ${changed.length} پکیج ذخیره شد.`, 'success');
    } catch (e) { notify(e instanceof Error ? e.message : 'ذخیره ترکیب پکیج‌ها انجام نشد.', 'error'); }
    finally { setSaving(false); }
  };

  const apply = async () => {
    const selected = packages?.find((item) => item.ID === applyPackageId);
    if (!selected || !selectedPortals.size) { notify('یک پکیج و حداقل یک پورتال را انتخاب کنید.', 'warning'); return; }
    if (dirtyPackageIds.has(selected.ID)) { notify('ابتدا تغییرات پکیج انتخاب‌شده را ذخیره کنید.', 'warning'); return; }
    if (!window.confirm(`Snapshot امکانات پکیج «${selected.Title}» روی ${selectedPortals.size} پورتال جایگزین شود؟ سقف تعداد فرم، اجازه ایجاد/ویرایش و Bootstrap تغییر نمی‌کند.`)) return;
    try {
      const response = await applyFeaturePackage(selected.ID, [...selectedPortals]);
      notify(`پکیج روی ${response.PortalCount} پورتال اعمال شد.`, 'success');
      setSelectedPortals(new Set());
      await load();
    } catch (e) { notify(e instanceof Error ? e.message : 'اعمال پکیج انجام نشد.', 'error'); }
  };

  if (error) return <ErrorMessage error={error}/>;
  if (!catalog || !packages) return <Loading/>;

  return <section className="df-feature-admin-page">
    <header className="df-feature-page-header"><div><span>فقط کاربر ارشد DNN</span><h1>مدیریت ترکیب پکیج‌های امکانات</h1><p>هر ردیف یک قابلیت و هر ستون یک پکیج است. برای انتقال، تیک پکیج مبدأ را بردارید و پکیج مقصد را فعال کنید؛ یک قابلیت می‌تواند عضو چند پکیج باشد.</p></div></header>

    <div className="df-feature-card">
      <div className="df-feature-card-head"><div><h2>ماتریس امکانات پکیج‌ها</h2><p>قابلیت‌های اجباری در همه پکیج‌ها قفل هستند. تغییر تعریف پکیج، مشتریان فعلی را خودکار تغییر نمی‌دهد تا زمانی که Snapshot روی پورتال اعمال شود.</p></div><button type="button" className="btn btn-primary" disabled={saving || dirtyPackageIds.size === 0} onClick={() => void saveAll()}>{saving ? 'در حال ذخیره…' : `ذخیره تغییرات${dirtyPackageIds.size ? ` (${dirtyPackageIds.size})` : ''}`}</button></div>
      <div className="table-responsive df-feature-matrix-wrap"><table className="table table-bordered df-feature-matrix"><thead><tr><th>قابلیت</th>{packages.map((pkg) => <th key={pkg.ID}><strong>{pkg.Title}</strong><small>تعریف v{pkg.DefinitionVersion}<br/>{pkg.AssignedPortalCount} پورتال</small>{dirtyPackageIds.has(pkg.ID) ? <em>تغییرکرده</em> : null}</th>)}</tr></thead><tbody>
        {groups.map(([category, features]) => <FragmentRows key={category} category={category} features={features} packages={packages} mandatory={mandatory} toggle={toggle}/>) }
      </tbody></table></div>
    </div>

    <div className="df-feature-card">
      <div className="df-feature-card-head"><div><h2>اعمال Snapshot پکیج روی پورتال‌ها</h2><p>این عملیات فقط فهرست قابلیت‌ها را جایگزین می‌کند؛ تعداد فرم، اجازه ایجاد/ویرایش، Bootstrap و Policyهای Access Control دست‌نخورده می‌مانند.</p></div><button type="button" className="btn btn-warning" onClick={() => void apply()}>اعمال روی پورتال‌های انتخابی</button></div>
      <label className="df-feature-portal-select"><span>پکیج مقصد</span><select className="form-control" value={applyPackageId ?? ''} onChange={(event) => setApplyPackageId(Number(event.target.value))}>{packages.map((pkg) => <option key={pkg.ID} value={pkg.ID}>{pkg.Title} — تعریف v{pkg.DefinitionVersion}</option>)}</select></label>
      <div className="table-responsive"><table className="table table-striped"><thead><tr><th></th><th>پورتال</th><th>پکیج فعلی</th><th>فرم‌ها</th><th>Bootstrap</th></tr></thead><tbody>
        {portals.map((portal) => <tr key={portal.PortalID}><td><input type="checkbox" checked={selectedPortals.has(portal.PortalID)} onChange={(event) => setSelectedPortals((current) => { const next = new Set(current); event.target.checked ? next.add(portal.PortalID) : next.delete(portal.PortalID); return next; })}/></td><td>{portal.PortalName} <small>#{portal.PortalID}</small></td><td>{portal.PackageTitle || 'سفارشی'}</td><td>{portal.FormCount}{portal.MaxForms == null ? ' / نامحدود' : ` / ${portal.MaxForms}`}</td><td>{portal.BootstrapVersion}</td></tr>)}
      </tbody></table></div>
    </div>
  </section>;
}

function FragmentRows({ category, features, packages, mandatory, toggle }: {
  category: string;
  features: FeatureCatalogItem[];
  packages: FeaturePackage[];
  mandatory: Set<string>;
  toggle: (packageId: number, key: string, checked: boolean) => void;
}) {
  return <>
    <tr className="df-feature-matrix-category"><th colSpan={packages.length + 1}>{category}</th></tr>
    {features.map((feature) => <tr key={feature.FeatureKey}>
      <th><strong>{feature.Title}</strong><small>{feature.Description || feature.FeatureKey}</small>{feature.IsMandatory ? <em>اجباری</em> : null}</th>
      {packages.map((pkg) => { const checked = feature.IsMandatory || pkg.FeatureKeys.includes(feature.FeatureKey); return <td key={pkg.ID}><label title={`${feature.Title} — ${pkg.Title}`}><input type="checkbox" checked={checked} disabled={feature.IsMandatory} onChange={(event) => toggle(pkg.ID, feature.FeatureKey, event.target.checked)}/><span>{checked ? 'فعال' : 'غیرفعال'}</span></label></td>; })}
    </tr>)}
  </>;
}
